<template>
  <f7-page>
    <div v-if="!loaded" class="alert-preloader-overlay">
      <div class="alert-preloader-card">
        <f7-preloader></f7-preloader>
        <div class="alert-preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <div v-else class="main-offer-card">
      <!-- Encabezado -->
      <div class="summary-header">
        <div class="summary-title">
          <div class="summary-icon-alerts"></div>
          <div class="summary-text">
            {{ $t('alert.paramsSummary') }}
          </div>
        </div>

        <!-- Botones -->
        <!-- Ambos botones juntos -->
        <div class="summary-actions">
          <button class="outlined-button" @click="goToAlertEdit">
            {{ $t('alert.edit') }}
          </button>
          <button
            class="view-sample-button"
            @click="goToSampleDetailsWithFilters"
          >
            {{ $t('viewSample') }}
          </button>
        </div>
      </div>

      <!-- Contenido principal -->
      <div class="alert-details-container">
        <!-- Card gris para los datos generales -->
        <div class="summary-gray-card">
          <div class="summary-gray-content">
            <f7-row class="info-row">
              <f7-col width="25">
                <div class="info-item">
                  <strong>{{ $t('company') }}:</strong>
                  <span>{{ companyName }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>{{ $t('address') }}:</strong>
                  <span>{{ addressName }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>{{ $t('project') }}:</strong>
                  <span>{{ details.study || '-' }}</span>
                </div>
              </f7-col>
            </f7-row>

            <f7-row class="info-row">
              <f7-col width="25">
                <div class="info-item">
                  <strong>
                    {{ $t('agronomySample.summary.sampleCode') }}:
                  </strong>
                  <span>{{ details.sample_code }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>{{ $t('atc') }}:</strong>
                  <span>{{ details.atc || '-' }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>
                    {{ $t('agronomySample.summary.sampleType') }}:
                  </strong>
                  <span>{{ details.sample_type }}</span>
                </div>
              </f7-col>
            </f7-row>

            <f7-row class="info-row">
              <f7-col width="25">
                <div class="info-item">
                  <strong>
                    {{ $t('environmentSamples.samplingPoint') }}:
                  </strong>
                  <span>{{ details.parcel }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>
                    {{ $t('foodSamples.samplingSite') }}:
                  </strong>
                  <span>{{ details.farm }}</span>
                </div>
              </f7-col>
              <f7-col width="25">
                <div class="info-item">
                  <strong>
                    {{ $t('agronomySample.summary.description') }}:
                  </strong>
                  <span>{{ details.sample_name || '' }}</span>
                </div>
              </f7-col>
            </f7-row>
          </div>
        </div>
        <!-- Tabla de parámetros -->
        <div class="marginTop15">
          <ParamsDetailsTable :details="details" />
        </div>
      </div>
    </div>
  </f7-page>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ParamsDetailsTable from '@/components/Alerts/Reports/DetailsPopup/Params/Table/index.vue';
import { resolveAtcKey } from '@/utils/atcRouting';

export default {
  name: 'ParamsDetailsPage',
  components: {
    ParamsDetailsTable,
  },
  props: {
    details: Object,
  },
  data() {
    return {
      loaded: true,
    };
  },
  computed: {
    ...mapState('contact', ['companyName', 'addressName']),
  },
  methods: {
    goBack() {
      this.$f7router.back();
    },
    goToAlertEdit() {
      this.$f7router.navigate({ name: 'AlertsParamsPage' });
    },
    async goToSampleDetailsWithFilters() {
      this.loaded = false;
      try {
        this.setReportSampleCode(this.details.sample_code);

        const currentSample = await this.getCurrentSampleByCode();
        const currentSampleId =
          currentSample?.id ||
          currentSample?._id ||
          currentSample?.sample_id;

        if (!currentSampleId) return;

        this.setCurrentSampleId(currentSampleId);

        await this.goToSampleDetailsByAtc(
          currentSample?.atc_name,
          currentSample
        );
      } finally {
        this.loaded = true;
      }
    },
    async goToSampleDetailsByAtc(atcName) {
      const atcKey = resolveAtcKey({
        atc_name: atcName,
        atc_code: this.details?.atc_code,
      });

      if (atcKey === 'FOOD') {
        this.setCurrentFilter('foodDetails');
        this.setFoodDetailsSelectedParameters(
          this.details.parameters.flatMap((param) => param.parameter)
        );

        this.$f7router.navigate(
          {
            name: 'FoodSampleDetailPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'AGRONOMY') {
        this.setCurrentFilter('agronomy');

        this.$f7router.navigate(
          {
            name: 'AgronomySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'ENVIRONMENT') {
        this.setCurrentFilter('environmentDetails');
        this.setEnvironmentDetailsSelectedParameters(
          this.details.parameters.flatMap((param) => param.parameter)
        );

        this.$f7router.navigate(
          {
            name: 'EnvironmentSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'MINING') {
        this.setCurrentFilter('miningDetails');
        this.setMiningDetailsSelectedParameters(
          this.details.parameters.flatMap((param) => param.parameter)
        );

        this.$f7router.navigate(
          {
            name: 'MiningSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'HEALTH_SECURITY') {
        this.setCurrentFilter('healthSecurityDetails');
        this.setHealthSecurityDetailsSelectedParameters(
          this.details.parameters.flatMap((param) => param.parameter)
        );

        this.$f7router.navigate(
          {
            name: 'HealthSecuritySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      }
    },
    ...mapActions('filter', [
      'setCurrentFilter',
      'setCurrentSampleId',
      'applyCurrentFilters',
    ]),
    ...mapActions('samples', [
      'getCurrentSampleByCode',
      'setReportSampleCode',
    ]),
    ...mapActions('foodDetailsFilter', {
      setFoodDetailsSelectedParameters: 'setSelectedParameters',
    }),
    ...mapActions('environmentDetailsFilter', {
      setEnvironmentDetailsSelectedParameters:
        'setSelectedParameters',
    }),
    ...mapActions('miningDetailsFilter', {
      setMiningDetailsSelectedParameters: 'setSelectedParameters',
    }),
    ...mapActions('healthSecurityDetailsFilter', {
      setHealthSecurityDetailsSelectedParameters:
        'setSelectedParameters',
    }),
  },
};
</script>

<style scoped>
.alert-preloader-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.alert-preloader-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 36px;
  min-width: 140px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.alert-preloader-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--Primary-Blue-60, #3e3c44);
}

.main-offer-card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(87, 84, 95, 0.3);
  padding: 24px; /* 24px en todos los lados */
  border: none;
  overflow-x: clip;
  display: flex;
  flex-direction: column;
  gap: 24px; /* Espaciado interno entre secciones igual que en Figma */
}

/* Encabezado */
.summary-header {
  margin: 0; /* Quitamos el margin-bottom */
}
.summary-title {
  gap: 10px;
}
.summary-icon-alerts {
  width: 24px;
  height: 24px;
  background-image: url('@/static/icons/ic_alerts.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.summary-text {
  font-size: 18px;
  font-weight: 600;
  color: #00346a;
}
.close-button {
  font-size: 20px;
  color: #00346a;
}

/* Card gris */
.summary-gray-card {
  background-color: #f7f7f8;
  border-radius: 10px;
  padding: 16px 24px;
  margin-bottom: 24px;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.summary-gray-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 15px;
  line-height: 1.4;
}
.info-item strong {
  font-weight: 600;
  color: #00346a;
}
.info-item span {
  color: #1a1a1a;
}

/* Botones */
.button-group {
  display: flex;
  gap: 10px;
  margin-top: 24px;
}
.standard-button {
  background-color: #00346a;
  color: #fff;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}
.standard-button:hover {
  background-color: #004b93;
}

/* Botón contorneado (Editar alerta) */
.outlined-button {
  background-color: #fff;
  border: 1.5px solid #00346a;
  color: #00346a;
  font-weight: 500;
  border-radius: 10px;
  height: 40px; /* Altura exacta de Figma */
  padding: 0 20px; /* Espaciado lateral equilibrado */
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap; /* Evita el salto de línea */
  transition: background-color 0.2s ease;
}
.outlined-button:hover {
  background-color: #f0f6ff;
}

/* Botón azul (Ver muestra) */
.view-sample-button {
  background-color: #00346a;
  color: white;
  border-radius: 10px;
  border: none;
  height: 40px; /* Misma altura */
  padding: 0 20px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}
.view-sample-button:hover {
  background-color: #004b93;
}

.summary-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.summary-actions {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  justify-content: flex-end; /* pegados a la derecha */
  gap: 12px; /* separación entre botones */
  width: auto !important;
}
.summary-actions button {
  width: auto !important;
  display: inline-flex !important;
}
</style>
